// FASE 05 — Cadastros Gerais (plano de 23/09/2026). Serviço comum aos cadastros da Gerência.
//
// Os controllers de Gerência respondem pelo BaseController do backend:
//   listagem paginada -> { statusCode, success, totalRecords, page, data: [...] }
//   demais respostas  -> { statusCode, success, data: ... }
//   validação         -> 400 { success: false, messages: [...] }
import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

export interface IParametrosListagem {
  page?: number;
  pageSize?: number;
  search?: string;
  [extra: string]: unknown;
}

export interface IPaginaCadastro<T> {
  itens: T[];
  total: number;
}

export async function listarCadastro<T>(
  endpoint: string,
  params?: IParametrosListagem
): Promise<IPaginaCadastro<T>> {
  const { page, pageSize, search, ...extras } = params ?? {};
  try {
    const { data } = await api.get(endpoint, {
      params: { page: page ?? 1, limit: pageSize ?? 10, filtrar: search ?? "", ...extras },
    });
    return {
      itens: Array.isArray(data?.data) ? (data.data as T[]) : [],
      total: data?.totalRecords ?? 0,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar a listagem");
    return { itens: [], total: 0 };
  }
}

/** Lista tudo de uma vez, para os campos de seleção (combos). */
export async function listarTodos<T>(endpoint: string): Promise<T[]> {
  const pagina = await listarCadastro<T>(endpoint, { page: 1, pageSize: 5000 });
  return pagina.itens;
}

export async function buscarCadastro<T>(endpoint: string, id: number): Promise<T | null> {
  try {
    const { data } = await api.get(`${endpoint}/${id}`);
    return (data?.data as T) ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar o cadastro");
    return null;
  }
}

export async function salvarCadastro<R = unknown>(endpoint: string, payload: object): Promise<R> {
  try {
    const { data } = await api.post(endpoint, payload);
    return data?.data as R;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível salvar o cadastro");
    throw error;
  }
}

export async function atualizarCadastro<R = unknown>(endpoint: string, payload: object): Promise<R> {
  try {
    const { data } = await api.put(endpoint, payload);
    return data?.data as R;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível salvar a alteração");
    throw error;
  }
}

export async function excluirCadastro(endpoint: string, campoId: string, id: number): Promise<void> {
  try {
    await api.delete(endpoint, { params: { [campoId]: id } });
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir o cadastro");
    throw error;
  }
}

export async function enviarArquivo<R = unknown>(endpoint: string, arquivo: File): Promise<R> {
  const formulario = new FormData();
  formulario.append("arquivo", arquivo);
  try {
    const { data } = await api.post(endpoint, formulario, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data?.data as R;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível enviar o arquivo");
    throw error;
  }
}

// Tipo de material / tipo de produto: as 5 opções do cadastro legado (Cores, Grupos e Referências).
export const TIPOS_MATERIAL = [
  { campo: "perfil", label: "Perfis" },
  { campo: "componente", label: "Componentes" },
  { campo: "vidro", label: "Vidros" },
  { campo: "superficie", label: "Superfícies" },
  { campo: "kit", label: "Kits" },
] as const;

export type CampoTipoMaterial = (typeof TIPOS_MATERIAL)[number]["campo"];
